import React, { useState, useCallback, useEffect } from 'react';
import {
    Box,
    Typography,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Paper,
    Grid,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    CircularProgress,
    Alert,
    IconButton,
    Chip,
    Tooltip,
    Snackbar,
    LinearProgress,
} from '@mui/material';
import { 
    Add as AddIcon, 
    Delete as DeleteIcon, 
    ArrowBack as ArrowBackIcon,
    Refresh as RefreshIcon,
    Link as LinkIcon,
} from '@mui/icons-material';
import { TreeSkill } from '../types/microsoftGraph';
import { api, API_ENDPOINTS } from '../config/api';
import { useAuth } from '../hooks/useAuth';
import { useParams, useNavigate } from 'react-router-dom';
import { useSkillsCache } from '../context/skillsCacheContext';

const SkillDetails: React.FC = () => {
    const { token } = useAuth();
    const navigate = useNavigate();
    const { skillID } = useParams<{ skillID?: string }>();
    
    // Use the skills tree cache
    const { 
        skills: treeSkills, 
        loading: cacheLoading, 
        error: cacheError, 
        fetchSkills, 
        refreshSkills,
        findSkillsByName,
        getSkillById,
        getAvailableSkillsForLinking,
        hasSkills,
        lastUpdated
    } = useSkillsCache();
    
    const [selectedSkill, setSelectedSkill] = useState<any>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [successMessage, setSuccessMessage] = useState<string | null>(null);
    const [linkDialogOpen, setLinkDialogOpen] = useState(false);
    const [linkType, setLinkType] = useState<'parent' | 'offspring' | null>(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [isRefreshingCache, setIsRefreshingCache] = useState(false);
    const [isCreateMode, setIsCreateMode] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);

    useEffect(() => {
        const initializeData = async () => {
            // Wait for token to be available
            if (!token) {
                return;
            }

            try {
                // Fetch skills tree if not already loaded
                if (!hasSkills) {
                    await fetchSkills();
                }
                
                if (skillID) {
                    // Edit mode - load existing skill
                    await handleSelectSkillForEdit(parseInt(skillID));
                } else {
                    // Create mode - initialize empty skill
                    setIsCreateMode(true);
                    setSelectedSkill({
                        skillID: 0,
                        skillName: '',
                        skillDescription: '',
                        criteria1: '',
                        criteria2: '',
                        criteria3: '',
                        criteria4: '',
                        criteria5: '',
                        hidden: 0,
                        parentSkills: [],
                        offspringSkills: []
                    });
                }
            } catch (err) {
                console.error('Failed to initialize skill data:', err);
                setError('Failed to initialize skill data');
            }
        };
        
        initializeData();
    }, [skillID, token, hasSkills]); // Added proper dependencies

    const handleSelectSkillForEdit = async (skillID: number) => {
        setLoading(true);
        setError(null);
        setIsCreateMode(false);
        
        try {
            const treeSkill = getSkillById(skillID);
            
            if (!treeSkill) {
                throw new Error('Skill not found in skills tree');
            }
            
            const fullSkill = await api.get(API_ENDPOINTS.skills.get(skillID));
            
            fullSkill.parentSkills = fullSkill.parentSkills || [];
            fullSkill.offspringSkills = fullSkill.offspringSkills || [];
            
            setSelectedSkill(fullSkill);
            
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : 'Failed to load skill';
            setError(errorMessage);
            
            const treeSkill = getSkillById(skillID);
            if (treeSkill) {
                setSelectedSkill({
                    skillID: treeSkill.skillID,
                    skillName: treeSkill.skillName,
                    skillDescription: '',
                    criteria1: '',
                    criteria2: '',
                    criteria3: '',
                    criteria4: '',
                    criteria5: '',
                    hidden: 0,
                    parentSkills: [],
                    offspringSkills: []
                });
            }
        } finally {
            setLoading(false);
        }
    };

    const handleRefreshCache = async () => {
        setIsRefreshingCache(true);
        try {
            await refreshSkills();
            setSuccessMessage('Skills tree cache refreshed successfully');
            
            if (selectedSkill?.skillID) {
                await handleSelectSkillForEdit(selectedSkill.skillID);
            }
        } catch (err) {
            setError('Failed to refresh cache');
            console.error('Cache refresh error:', err);
        } finally {
            setIsRefreshingCache(false);
        }
    };

    const handleFieldChange = (field: string, value: any) => {
        if (selectedSkill) {
            setSelectedSkill({ ...selectedSkill, [field]: value });
        }
    };

    const handleSaveSkill = async () => {
        if (!selectedSkill) return;
        setLoading(true);
        setError(null);
        
        try {
            const payload = {
                skillID: selectedSkill.skillID || 0,
                skillName: selectedSkill.skillName?.trim() || '',
                skillDescription: selectedSkill.skillDescription?.trim() || '',
                criteria1: selectedSkill.criteria1?.trim() || '',
                criteria2: selectedSkill.criteria2?.trim() || '',
                criteria3: selectedSkill.criteria3?.trim() || '',
                criteria4: selectedSkill.criteria4?.trim() || '',
                criteria5: selectedSkill.criteria5?.trim() || '',
                hidden: selectedSkill.hidden || 0,
                developmentAge: selectedSkill.developmentAge 
                    ? parseFloat(selectedSkill.developmentAge) || 0 
                    : 0,
                parentSkills: (selectedSkill.parentSkills || []).map((p: any) => ({
                    skillID: p.skillID,
                    skillName: p.skillName
                })),
                offspringSkills: (selectedSkill.offspringSkills || []).map((o: any) => ({
                    skillID: o.skillID,
                    skillName: o.skillName
                }))
            };

            if (!payload.skillName) {
                throw new Error('Skill name is required');
            }

            const endpoint = isCreateMode 
                ? API_ENDPOINTS.skills.create
                : API_ENDPOINTS.skills.update(selectedSkill.skillID);

            console.log('Saving skill:', { endpoint, payload });

            await (isCreateMode 
                ? api.post(endpoint, payload)
                : api.put(endpoint, payload)
            );
            
            setSuccessMessage('Skill saved successfully!');
            
            await refreshSkills();
            setTimeout(() => navigate(-1), 1500);
            
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : 'Failed to save skill';
            console.error('Save skill error:', err);
            setError(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    const openLinkDialog = (type: 'parent' | 'offspring') => {
        setLinkType(type);
        setSearchTerm('');
        setLinkDialogOpen(true);
    };

    const handleAddLink = (treeSkill: TreeSkill) => {
        if (!selectedSkill || !linkType) return;

        const linkedSkill = {
            skillID: treeSkill.skillID,
            skillName: treeSkill.skillName,
        };

        const updatedSkill = { ...selectedSkill };
        
        if (linkType === 'parent') {
            const existingIds = (updatedSkill.parentSkills || []).map((p: any) => p.skillID);
            if (!existingIds.includes(treeSkill.skillID)) {
                updatedSkill.parentSkills = [...(updatedSkill.parentSkills || []), linkedSkill];
            } else {
                setError(`Skill "${treeSkill.skillName}" is already linked as a parent`);
                setLinkDialogOpen(false);
                return;
            }
        } else {
            const existingIds = (updatedSkill.offspringSkills || []).map((o: any) => o.skillID);
            if (!existingIds.includes(treeSkill.skillID)) {
                updatedSkill.offspringSkills = [...(updatedSkill.offspringSkills || []), linkedSkill];
            } else {
                setError(`Skill "${treeSkill.skillName}" is already linked as a child`);
                setLinkDialogOpen(false);
                return;
            }
        }
        
        setSelectedSkill(updatedSkill);
        setLinkDialogOpen(false);
        // ✅ Updated message to note that links need saving
        setSuccessMessage(`Added "${treeSkill.skillName}" as ${linkType} - remember to save!`);
    };

   const handleRemoveLink = (type: 'parent' | 'offspring', skillID: number) => {
        if (!selectedSkill) return;

        const updatedSkill = { ...selectedSkill };
        
        if (type === 'parent') {
            updatedSkill.parentSkills = (updatedSkill.parentSkills || []).filter((p: any) => p.skillID !== skillID);
        } else {
            updatedSkill.offspringSkills = (updatedSkill.offspringSkills || []).filter((o: any) => o.skillID !== skillID);
        }
        
        setSelectedSkill(updatedSkill);
        // ✅ Updated message to note that links need saving
        setSuccessMessage('Link removed - remember to save!');
    };

   const handleDeleteSkill = async () => {
        if (!selectedSkill?.skillID) return;
        
        setIsDeleting(true);
        setError(null);
        
        try {
            await api.delete(API_ENDPOINTS.skills.delete(selectedSkill.skillID));

            setSuccessMessage('Skill deleted successfully!');
            
            // ✅ Refresh cache to remove deleted skill and its links
            await refreshSkills();
            setTimeout(() => navigate(-1), 1000);
            
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : 'Failed to delete skill';
            console.error('Delete skill error:', err);
            setError(errorMessage);
        } finally {
            setIsDeleting(false);
            setDeleteDialogOpen(false);
        }
    };

    const renderLinkDialog = () => {
        if (!selectedSkill || !linkType) return null;
        
        // Get existing linked skill IDs
        const existingLinkIds = linkType === 'parent'
            ? (selectedSkill.parentSkills || []).map((p: any) => p.skillID)
            : (selectedSkill.offspringSkills || []).map((o: any) => o.skillID);
        
        // Get available skills from tree cache
        const availableSkills = getAvailableSkillsForLinking(
            selectedSkill.skillID, 
            existingLinkIds
        );
        
        // Filter by search term
        const filteredSkills = availableSkills.filter(skill =>
            !searchTerm || 
            skill.skillName.toLowerCase().includes(searchTerm.toLowerCase())
        );

        return (
            <Dialog open={linkDialogOpen} onClose={() => setLinkDialogOpen(false)} maxWidth="md" fullWidth>
                <DialogTitle>
                    Add {linkType === 'parent' ? 'Parent' : 'Child'} Skill
                </DialogTitle>
                <DialogContent>
                    <TextField
                        fullWidth
                        placeholder="Search skills..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        margin="normal"
                    />
                    <TableContainer sx={{ maxHeight: 400, mt: 2 }}>
                        <Table size="small" stickyHeader>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Skill Name</TableCell>
                                    <TableCell align="right">Action</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {filteredSkills.map((skill) => (
                                    <TableRow key={skill.skillID}>
                                        <TableCell>{skill.skillName}</TableCell>
                                        <TableCell align="right">
                                            <Button
                                                size="small"
                                                onClick={() => handleAddLink(skill)}
                                                startIcon={<LinkIcon />}
                                            >
                                                Add
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                ))}
                                {filteredSkills.length === 0 && (
                                    <TableRow>
                                        <TableCell colSpan={2} align="center">
                                            <Typography variant="body2" color="text.secondary">
                                                No skills available
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setLinkDialogOpen(false)}>Close</Button>
                </DialogActions>
            </Dialog>
        );
    };

    const renderDeleteConfirmDialog = () => (
        <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
            <DialogTitle>Confirm Delete</DialogTitle>
            <DialogContent>
                <Typography>
                    Are you sure you want to delete "{selectedSkill?.skillName}"?
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                    This will also remove all related links and cannot be undone.
                </Typography>
            </DialogContent>
            <DialogActions>
                <Button onClick={() => setDeleteDialogOpen(false)} disabled={isDeleting}>
                    Cancel
                </Button>
                <Button 
                    onClick={handleDeleteSkill} 
                    color="error" 
                    disabled={isDeleting}
                    startIcon={isDeleting ? <CircularProgress size={20} /> : <DeleteIcon />}
                >
                    {isDeleting ? 'Deleting...' : 'Delete'}
                </Button>
            </DialogActions>
        </Dialog>
    );

    return (
        <Box sx={{ p: 3, maxWidth: 1200, margin: '0 auto' }}>
            {/* Show loading state if token not ready */}
            {!token && (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
                    <CircularProgress />
                    <Typography sx={{ ml: 2 }}>Loading authentication...</Typography>
                </Box>
            )}

            {/* Show main content only when token is available */}
            {token && (
                <>
                    {/* Header */}
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, justifyContent: 'space-between' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <IconButton onClick={() => navigate(-1)} sx={{ mr: 2 }}>
                                <ArrowBackIcon />
                            </IconButton>
                            <Typography variant="h4">
                                {isCreateMode ? 'Create New Skill' : 'Edit Skill'}
                            </Typography>
                        </Box>
                        
                        {/* Delete button - only show in edit mode */}
                        {!isCreateMode && selectedSkill?.skillID && (
                            <Button
                                variant="outlined"
                                color="error"
                                startIcon={<DeleteIcon />}
                                onClick={() => setDeleteDialogOpen(true)}
                                disabled={loading || isDeleting}
                            >
                                Delete Skill
                            </Button>
                        )}
                    </Box>

                    {/* Cache Status Alert */}
                    {cacheError && (
                        <Alert 
                            severity="warning" 
                            onClose={() => {}} 
                            sx={{ mb: 2 }}
                            action={
                                <IconButton 
                                    onClick={handleRefreshCache}
                                    size="small"
                                    disabled={cacheLoading || isRefreshingCache}
                                >
                                    <RefreshIcon />
                                </IconButton>
                            }
                        >
                            Skills cache error: {cacheError}
                        </Alert>
                    )}

                    {/* Success Message */}
                    <Snackbar
                        open={!!successMessage}
                        autoHideDuration={6000}
                        onClose={() => setSuccessMessage(null)}
                        message={successMessage}
                    />

                    {/* Error Alert */}
                    {error && (
                        <Alert 
                            severity="error" 
                            onClose={() => setError(null)} 
                            sx={{ mb: 2 }}
                        >
                            {error}
                        </Alert>
                    )}

                    {/* Loading Progress */}
                    {(isRefreshingCache || loading) && <LinearProgress sx={{ mb: 2 }} />}

                    {/* Main Content */}
                    {selectedSkill && (
                        <Paper sx={{ p: 3 }}>
                            <Grid container spacing={3}>
                                {/* Left Column - Name & Description */}
                                <Grid item xs={12} md={6}>
                                    <TextField 
                                        label="Skill Name" 
                                        value={selectedSkill.skillName || ''} 
                                        onChange={(e) => handleFieldChange('skillName', e.target.value)} 
                                        fullWidth 
                                        margin="normal"
                                        required
                                    />
                                    <TextField 
                                        label="Skill Description" 
                                        value={selectedSkill.skillDescription || ''} 
                                        onChange={(e) => handleFieldChange('skillDescription', e.target.value)} 
                                        fullWidth 
                                        multiline 
                                        rows={4} 
                                        margin="normal" 
                                    />
                                </Grid>

                                {/* Right Column - Criteria */}
                                <Grid item xs={12} md={6}>
                                    <TextField 
                                        label="Criteria 1 Mark" 
                                        value={selectedSkill.criteria1 || ''} 
                                        onChange={(e) => handleFieldChange('criteria1', e.target.value)} 
                                        fullWidth 
                                        multiline 
                                        rows={2} 
                                        margin="normal" 
                                    />
                                    <TextField 
                                        label="Criteria 2 Marks" 
                                        value={selectedSkill.criteria2 || ''} 
                                        onChange={(e) => handleFieldChange('criteria2', e.target.value)} 
                                        fullWidth 
                                        multiline 
                                        rows={2} 
                                        margin="normal" 
                                    />
                                    <TextField 
                                        label="Criteria 3 Marks" 
                                        value={selectedSkill.criteria3 || ''} 
                                        onChange={(e) => handleFieldChange('criteria3', e.target.value)} 
                                        fullWidth 
                                        multiline 
                                        rows={2} 
                                        margin="normal" 
                                    />
                                    <TextField 
                                        label="Criteria 4 Marks" 
                                        value={selectedSkill.criteria4 || ''} 
                                        onChange={(e) => handleFieldChange('criteria4', e.target.value)} 
                                        fullWidth 
                                        multiline 
                                        rows={2} 
                                        margin="normal" 
                                    />
                                    <TextField 
                                        label="Criteria 5 Marks" 
                                        value={selectedSkill.criteria5 || ''} 
                                        onChange={(e) => handleFieldChange('criteria5', e.target.value)} 
                                        fullWidth 
                                        multiline 
                                        rows={2} 
                                        margin="normal" 
                                    />
                                </Grid>

                                {/* Parent Skills Table */}
                                <Grid item xs={12} md={6}>
                                    <Typography variant="h6">Parent Skills</Typography>
                                    <Button onClick={() => openLinkDialog('parent')} startIcon={<AddIcon />} sx={{ mb: 2 }}>
                                        Add Parent
                                    </Button>
                                    <TableContainer component={Paper} variant="outlined">
                                        <Table size="small">
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell>Skill Name</TableCell>
                                                    <TableCell align="right">Action</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {(selectedSkill.parentSkills || []).map((p: any) => (
                                                    <TableRow key={p.skillID}>
                                                        <TableCell>{p.skillName}</TableCell>
                                                        <TableCell align="right">
                                                            <IconButton onClick={() => handleRemoveLink('parent', p.skillID)} size="small">
                                                                <DeleteIcon />
                                                            </IconButton>
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                                {(selectedSkill.parentSkills || []).length === 0 && (
                                                    <TableRow>
                                                        <TableCell colSpan={2} align="center">
                                                            <Typography variant="body2" color="text.secondary">
                                                                No parent skills linked
                                                            </Typography>
                                                        </TableCell>
                                                    </TableRow>
                                                )}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                </Grid>

                                {/* Offspring Skills Table */}
                                <Grid item xs={12} md={6}>
                                    <Typography variant="h6">Offspring Skills</Typography>
                                    <Button onClick={() => openLinkDialog('offspring')} startIcon={<AddIcon />} sx={{ mb: 2 }}>
                                        Add Child
                                    </Button>
                                    <TableContainer component={Paper} variant="outlined">
                                        <Table size="small">
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell>Skill Name</TableCell>
                                                    <TableCell align="right">Action</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {(selectedSkill.offspringSkills || []).map((o: any) => (
                                                    <TableRow key={o.skillID}>
                                                        <TableCell>{o.skillName}</TableCell>
                                                        <TableCell align="right">
                                                            <IconButton onClick={() => handleRemoveLink('offspring', o.skillID)} size="small">
                                                                <DeleteIcon />
                                                            </IconButton>
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                                {(selectedSkill.offspringSkills || []).length === 0 && (
                                                    <TableRow>
                                                        <TableCell colSpan={2} align="center">
                                                            <Typography variant="body2" color="text.secondary">
                                                                No offspring skills linked
                                                            </Typography>
                                                        </TableCell>
                                                    </TableRow>
                                                )}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                </Grid>
                            </Grid>

                            {/* Save Button */}
                            <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                                <Button onClick={() => navigate(-1)}>
                                    Cancel
                                </Button>
                                <Button 
                                    onClick={handleSaveSkill} 
                                    variant="contained" 
                                    disabled={loading || !selectedSkill.skillName}
                                >
                                    {loading ? <CircularProgress size={24} /> : 'Save Skill'}
                                </Button>
                            </Box>
                        </Paper>
                    )}

                    {/* Link Dialog */}
                    {renderLinkDialog()}

                    {/* Delete Confirmation Dialog */}
                    {renderDeleteConfirmDialog()}
                </>
            )}
        </Box>
    );
};

export default SkillDetails;